import { useEffect, useRef, useState } from 'react';

import { Box, Typography } from '@mui/material';

import { cx } from '@/styles/muiTheme';
import { getEventStyle } from '../../services/clipEventStyle';
import type { ClipLibraryItem } from '../../services/toClipLibraryItems';
import TeamCrest from './TeamCrest';

type Props = {
  clip: ClipLibraryItem;
  /** Opens the clip in the player dialog. */
  onOpen: () => void;
};

const ClipCard = ({ clip, onOpen }: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  // Sticky: the first hover mounts the <video> and keeps it mounted, so a second
  // hover replays from buffer. Without this gate all ~200 cards would request
  // their mp4 on mount, which is the whole reason previews are hover-driven.
  const [isMounted, setIsMounted] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  // Gates the fade-in so we cross-fade to a decoded frame, not to black.
  const [canPlay, setCanPlay] = useState(false);

  const hasPreview = Boolean(clip.previewMp4Url);
  const eventStyle = getEventStyle(clip.eventKey);

  const activate = () => {
    if (!hasPreview) return;
    setIsMounted(true);
    setIsHovering(true);
  };

  const deactivate = () => setIsHovering(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isHovering) {
      // play() rejects with AbortError when the pointer leaves before the
      // promise settles. That is the expected path out, not a failure.
      video.play().catch(() => {});
      return;
    }

    video.pause();
    video.currentTime = 0;
  }, [isHovering, isMounted]);

  return (
    <Box
      onMouseEnter={activate}
      onMouseLeave={deactivate}
      onFocus={activate}
      onBlur={deactivate}
      onClick={onOpen}
      // The card is a div, so the two keys a real <button> would handle have to
      // be wired by hand. Space is prevented as well as handled, or it scrolls.
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onOpen();
      }}
      role="button"
      // Always focusable now that the whole card opens the player; it used to be
      // reachable only when there was a preview to trigger on focus.
      tabIndex={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        borderRadius: '6px',
        padding: '6px',
        margin: '-6px',
        outline: 'none',
        cursor: 'pointer',
        transition: 'background-color 120ms ease',
        '&:hover, &:focus-visible': { backgroundColor: cx.bg.surface2 },
        '&:focus-visible': { boxShadow: `0 0 0 2px ${cx.blue.main}` },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '16 / 9',
          borderRadius: '4px',
          overflow: 'hidden',
          backgroundColor: cx.bg.surface3,
          backgroundImage: clip.thumbnailUrl ? `url(${clip.thumbnailUrl})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {isMounted && clip.previewMp4Url ? (
          <Box
            component="video"
            ref={videoRef}
            src={clip.previewMp4Url}
            muted
            loop
            playsInline
            preload="none"
            poster={clip.thumbnailUrl ?? undefined}
            onCanPlay={() => setCanPlay(true)}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: isHovering && canPlay ? 1 : 0,
              transition: 'opacity 160ms ease',
              pointerEvents: 'none',
            }}
          />
        ) : null}

        {/* Absolute overlays sit above the video so the badges survive playback. */}
        {clip.eventKey !== 'none' ? (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              height: 20,
              padding: '0 8px',
              borderRadius: '100px',
              display: 'flex',
              alignItems: 'center',
              fontSize: 10,
              letterSpacing: '0.15px',
              backgroundColor: eventStyle.bg,
              color: eventStyle.fg,
            }}
          >
            {clip.eventLabel}
          </Box>
        ) : null}

        {clip.isoLabel ? (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              height: 20,
              maxWidth: 'calc(100% - 70px)',
              padding: '0 8px',
              borderRadius: '100px',
              backgroundColor: 'rgba(9,9,12,0.85)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: 10,
              letterSpacing: '0.15px',
              color: cx.light[100],
            }}
          >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: cx.green, flexShrink: 0 }} />
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              ISO {clip.isoLabel}
            </Box>
          </Box>
        ) : null}

        {clip.duration ? (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              height: 20,
              padding: '0 6px',
              borderRadius: '4px',
              backgroundColor: 'rgba(9,9,12,0.85)',
              display: 'flex',
              alignItems: 'center',
              fontFamily: cx.fontMono,
              fontSize: 11,
              color: cx.text.primary,
            }}
          >
            {clip.duration}
          </Box>
        ) : null}

        {/* Exported clips always carry preview assets, so a missing preview means
            the clip is still moving through the pipeline (or predates them). */}
        {!hasPreview ? (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              height: 20,
              padding: '0 8px',
              borderRadius: '100px',
              backgroundColor: 'rgba(9,9,12,0.85)',
              display: 'flex',
              alignItems: 'center',
              fontSize: 10,
              letterSpacing: '0.15px',
              color: cx.text.tertiary,
            }}
          >
            No preview
          </Box>
        ) : null}
      </Box>

      <Box sx={{ display: 'flex', gap: '10px' }}>
        <TeamCrest
          abbreviation={clip.teamAbbreviation}
          logoUrl={clip.teamLogoUrl}
          color={clip.teamColor}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, lineHeight: '20px', letterSpacing: '0.15px', color: cx.text.primary }}>
            {clip.title}
          </Typography>
          <Typography sx={{ fontSize: 12, lineHeight: '16px', letterSpacing: '0.15px', color: cx.text.secondary }}>
            {clip.gameLabel}
          </Typography>
          {clip.meta ? (
            <Typography sx={{ fontSize: 11, lineHeight: '14px', letterSpacing: '0.15px', color: cx.text.tertiary }}>
              {clip.meta}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default ClipCard;
